// ─── Emp_Files_Page ─────────────────────────────────────────────────────
// Shared page used by both Emp_Form16_Files and Emp_Healthcard_Files. The
// `kind` prop ("form16" | "healthcard") selects the backend table and the
// upload folder; UI shape is identical.
//
// Layout:
//   • big "+ Add" button at the top — click toggles an inline upload row
//   • row contains emp dropdown / year dropdown / Upload1 / Upload2 / Upload3 / Save
//   • each Upload button opens a file picker (any file type, 5 MB max each)
//   • below the Add control, a table lists every existing record with
//     Year, Employee, the three filenames (each downloadable), and a
//     delete action.
// ───────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import Select from "react-select";
import { FaPlus, FaSave, FaUpload, FaTrash, FaDownload, FaTimes, FaCheckCircle, FaFolderOpen, FaEdit, FaSyncAlt, FaSearch, FaFileExcel, FaFilePdf, FaPaperPlane, FaEnvelope, FaHistory, FaEye } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import { exportTicketsExcel, exportTicketsPdf } from "./utils/ticketExport";
import ExportMenu from "./ExportMenu";
import { DSLIP_YEAR_MIN, dslipYearMax, DSLIP_YEAR_SELECT } from "./utils/dSlip_years";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Emp_Files_Page.css";

const MAX_FILE_BYTES = 5 * 1024 * 1024;        // 5 MB per file

// Upper bound for the Year dropdown — covers retroactive uploads + a few
// years ahead. Lower bound is set per-employee from their DOJ year so
// admin can't accidentally upload a doc for a year before the employee
// even joined.
const YEAR_MAX = dslipYearMax();
const YEAR_HARD_MIN = DSLIP_YEAR_MIN;   // floor for every dSlip year dropdown

// Matches the Month/Year dropdowns in Payslip Management so every dSlip year
// picker looks the same (rounded card, slate border, orange focus ring).
const EF_YEAR_STYLES = {
    control: (p, s) => ({
        ...p,
        minHeight: 40,
        borderRadius: 8,
        borderColor: s.isFocused ? "#fb923c" : "#cbd5e1",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(251, 146, 60, 0.15)" : "none",
        "&:hover": { borderColor: "#fb923c" },
    }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#4a90e2" : s.isFocused ? "#f3f4f6" : "#fff",
        color: s.isSelected ? "#fff" : "#0d1b2a",
        padding: "10px 12px",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const TITLES = {
    form16:     { page: "Emp Form-16 Files",   subtitle: "Upload and manage Form-16 documents per employee per year." },
    healthcard: { page: "Emp Healthcard Files", subtitle: "Upload and manage Healthcard documents per employee per year." },
    eletter:    { page: "Emp E-Letter Files",  subtitle: "Upload and manage employee letters (bonus, hike, promotion, NOC…) per employee per year." },
};

// Sort options for the dropdown — same shape as Payslip_Salary_Structure
// so the visual treatment matches across pages.
const SORT_OPTIONS = [
    { value: "year_desc",     label: "Year (newest first)" },
    { value: "year_asc",      label: "Year (oldest first)" },
    { value: "emp_id_asc",    label: "Employee Id A → Z" },
    { value: "emp_id_desc",   label: "Employee Id Z → A" },
    { value: "name_asc",      label: "Name A → Z" },
    { value: "name_desc",     label: "Name Z → A" },
    { value: "uploaded_desc", label: "Recently uploaded" },
    { value: "uploaded_asc",  label: "First uploaded" },
];

// Two picks are the same document when the name and byte size both match.
// Browsers give no file path or hash, so this is the strongest fingerprint
// available client-side - and it catches the real mistake, which is picking
// the same file into two slots without noticing.
const sameFile = (a, b) => !!a && !!b && a.name === b.name && a.size === b.size;

const fmtBytes = (b) => {
    if (!b && b !== 0) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (s) => {
    if (!s) return "-";
    const d = new Date(s);
    if (isNaN(d)) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export default function Emp_Files_Page({ navSize, kind }) {
    const navigate = useNavigate();
    const meta = TITLES[kind] || TITLES.form16;
    // E-Letter is the only kind with a configurable "Letter Type" dropdown.
    const hasLetterType = kind === "eletter";

    // Active employees → react-select options
    const [employees, setEmployees] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    // Inline upload row state — only one open at a time
    const [showAdd, setShowAdd] = useState(false);
    const [pickEmp, setPickEmp] = useState(null);
    const [pickYear, setPickYear] = useState("");
    const [pickLetterType, setPickLetterType] = useState(null);   // eletter only
    const [file1, setFile1] = useState(null);
    const [file2, setFile2] = useState(null);
    const [file3, setFile3] = useState(null);
    const [saving, setSaving] = useState(false);

    // Configurable letter-type dropdown (eletter only) + its manage modal.
    const [letterTypes, setLetterTypes] = useState([]);
    const [showManageTypes, setShowManageTypes] = useState(false);

    // Email (notify employee that a document is available) — all kinds.
    // emailModal: { ids, recipients, index, previews, checked, loading, previewLoading, sending, result, error }
    const [emailModal, setEmailModal] = useState(null);
    const [mailFilters, setMailFilters] = useState({ emp_id: "", year: "" }); // recipient-picker filters
    const [emailHistory, setEmailHistory] = useState(null); // { scope, row, rows, loading }

    // Edit mode — when set, the row with this id renders inline edit
    // controls instead of the normal action buttons. `editFiles` holds
    // the NEW picks (file objects keyed by slot 1/2/3); `editClears` is
    // a Set of slot numbers the admin chose to delete.
    const [editingId, setEditingId] = useState(null);
    const [editFiles, setEditFiles] = useState({ 1: null, 2: null, 3: null });
    const [editClears, setEditClears] = useState(new Set());
    const [editSaving, setEditSaving] = useState(false);

    // Toolbar — search/sort
    const [search, setSearch] = useState("");
    const [sortOrder, setSortOrder] = useState("year_desc");
    const [empFilter, setEmpFilter] = useState("");     // export/view filter by employee
    const [yearFilter, setYearFilter] = useState("");   // export/view filter by year
    const [showSortDropdown, setShowSortDropdown] = useState(false);

    // Close sort dropdown on outside click
    useEffect(() => {
        if (!showSortDropdown) return;
        const onDocClick = (e) => {
            if (!e.target.closest("[data-ef-sort-dropdown]")) setShowSortDropdown(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [showSortDropdown]);

    // ─── Auth: admin/sub-admin only (same gate as the other admin pages) ──
    useEffect(() => {
        apiFetch("/api/employee/logined_employee", { method: "GET", credentials: "include" })
            .then((r) => r.json())
            .then((data) => {
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            })
            .catch(() => navigate("/login"));
    }, [navigate]);

    // ─── Initial fetches ──────────────────────────────────────────────
    const fetchEmployees = useCallback(async () => {
        try {
            const res = await apiFetch("/api/emp_files/employees");
            const data = await res.json();
            setEmployees(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ fetch employees:", err);
        }
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/api/emp_files/${kind}/list`);
            const data = await res.json();
            setRows(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(`❌ fetch ${kind} list:`, err);
        } finally {
            setLoading(false);
        }
    }, [kind]);

    // Letter-type options (eletter only). Includes usage_count per option so the
    // manage modal can warn before renaming/deleting one that's in use.
    const fetchLetterTypes = useCallback(async () => {
        if (kind !== "eletter") return;
        try {
            const res = await apiFetch("/api/emp_files/letter_types");
            const data = await res.json();
            setLetterTypes(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ fetch letter types:", err);
        }
    }, [kind]);

    useEffect(() => {
        fetchEmployees();
        fetchList();
        fetchLetterTypes();
    }, [fetchEmployees, fetchList, fetchLetterTypes]);

    // Active options for the Add dropdown (value = type_name snapshot).
    const activeLetterTypeOptions = useMemo(
        () => letterTypes.filter((t) => t.active).map((t) => ({ value: t.type_name, label: t.type_name })),
        [letterTypes]
    );

    // ─── Helpers ──────────────────────────────────────────────────────
    const empOptions = useMemo(
        () => employees.map((e) => ({
            value: e.emp_id,
            label: `${e.emp_first_name} ${e.emp_last_name}  ·  ${e.emp_id}`,
            joining_date: e.joining_date,
        })),
        [employees]
    );

    // Year options scope to the selected employee's joining year, AND
    // exclude years that emp already has a row for. Admin should use
    // Edit on the existing row rather than create a duplicate (emp, year).
    const yearOptions = useMemo(() => {
        if (!pickEmp) return [];
        const doj = pickEmp.joining_date;
        const dojYear = doj ? parseInt(doj.split("-")[0]) : NaN;
        // Never below 2018 (the dSlip floor), and never before the employee
        // joined - so a 2019 document can't be filed against a 2024 joiner.
        const lower = Number.isFinite(dojYear) ? Math.max(YEAR_HARD_MIN, dojYear) : YEAR_HARD_MIN;
        const takenYears = new Set(
            rows
                .filter((r) => r.emp_id === pickEmp.value)
                .map((r) => parseInt(r.year))
                .filter(Number.isFinite)
        );
        const ys = [];
        for (let y = YEAR_MAX; y >= lower; y--) {
            if (!takenYears.has(y)) ys.push(y);
        }
        return ys;
    }, [pickEmp, rows]);

    // Filter rows by the search box (name / emp_id / year) and apply the
    // selected sort order. Filenames are also searchable so admin can
    // jump to "the row with that PDF" quickly.
    // Dropdown options for the export/view filters, derived from the loaded rows.
    const empFilterOptions = useMemo(() => {
        const seen = new Map();
        rows.forEach((r) => {
            if (!seen.has(r.emp_id)) seen.set(r.emp_id, `${r.emp_first_name || ""} ${r.emp_last_name || ""}`.trim() || r.emp_id);
        });
        const list = [...seen].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => ({ value: id, label: `${name} (${id})` }));
        return [{ value: "", label: "All employees" }, ...list];
    }, [rows]);
    const yearFilterOptions = useMemo(() => {
        const years = [...new Set(rows.map((r) => r.year).filter((y) => y != null))].sort((a, b) => b - a);
        return [{ value: "", label: "All years" }, ...years.map((y) => ({ value: String(y), label: String(y) }))];
    }, [rows]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = rows;
        if (empFilter) list = list.filter((r) => r.emp_id === empFilter);
        if (yearFilter) list = list.filter((r) => String(r.year) === yearFilter);
        if (q) {
            list = list.filter((r) => {
                const haystack = [
                    `${r.emp_first_name || ""} ${r.emp_last_name || ""}`,
                    r.emp_id,
                    String(r.year || ""),
                    r.letter_type,
                    r.file1_name, r.file2_name, r.file3_name,
                ].filter(Boolean).join(" ").toLowerCase();
                return haystack.includes(q);
            });
        }
        const fullName = (r) => `${r.emp_first_name || ""} ${r.emp_last_name || ""}`.trim().toLowerCase();
        const t = (r) => new Date(r.uploaded_time || 0).getTime();
        const sorted = [...list];
        switch (sortOrder) {
            case "year_desc":     sorted.sort((a, b) => (b.year - a.year) || (t(b) - t(a))); break;
            case "year_asc":      sorted.sort((a, b) => (a.year - b.year) || (t(b) - t(a))); break;
            case "emp_id_asc":    sorted.sort((a, b) => String(a.emp_id).localeCompare(String(b.emp_id))); break;
            case "emp_id_desc":   sorted.sort((a, b) => String(b.emp_id).localeCompare(String(a.emp_id))); break;
            case "name_asc":      sorted.sort((a, b) => fullName(a).localeCompare(fullName(b))); break;
            case "name_desc":     sorted.sort((a, b) => fullName(b).localeCompare(fullName(a))); break;
            case "uploaded_desc": sorted.sort((a, b) => t(b) - t(a)); break;
            case "uploaded_asc":  sorted.sort((a, b) => t(a) - t(b)); break;
            default: break;
        }
        return sorted;
    }, [rows, search, sortOrder, empFilter, yearFilter]);

    // ── Export (Excel + PDF) via the shared ticket exporter ──────────────
    const KIND_LABEL = kind === "form16" ? "Form-16 Files" : kind === "healthcard" ? "Health Card Files" : "E-Letter Files";
    const buildExport = () => {
        const columns = [
            { key: "year",          label: "Year",          width: 10, pdfWidth: 55 },
            { key: "emp",           label: "Employee",      width: 26, pdfWidth: 130 },
            { key: "emp_id",        label: "Emp ID",        width: 14, pdfWidth: 70 },
            ...(hasLetterType ? [{ key: "letter_type", label: "Letter Type", width: 20, pdfWidth: 95 }] : []),
            { key: "files",         label: "Files",         width: 40, pdfWidth: 170, wrap: true },
            { key: "downloaded",    label: "Downloaded",    width: 14, pdfWidth: 80 },
            { key: "downloaded_at", label: "Downloaded at", width: 24, pdfWidth: 120 },
            { key: "emailed",       label: "Emailed",       width: 14, pdfWidth: 80 },
            { key: "emailed_at",    label: "Emailed at",    width: 24, pdfWidth: 120 },
            { key: "uploaded_at",   label: "Uploaded",      width: 22, pdfWidth: 110 },
        ];
        const data = filteredRows.map((r) => ({
            year: r.year,
            emp: `${r.emp_first_name || ""} ${r.emp_last_name || ""}`.trim() || r.emp_id,
            emp_id: r.emp_id,
            letter_type: r.letter_type || "-",
            files: [r.file1_name, r.file2_name, r.file3_name].filter(Boolean).join(", ") || "-",
            downloaded: r.downloaded ? "Yes" : "No",
            downloaded_at: r.downloaded_at ? new Date(r.downloaded_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "",
            emailed: r.emailed ? "Yes" : "No",
            emailed_at: r.emailed_at ? new Date(r.emailed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "",
            uploaded_at: fmtDate(r.uploaded_time),
        }));
        const filterLabel = [
            empFilter && `Employee: ${empFilterOptions.find((o) => o.value === empFilter)?.label || empFilter}`,
            yearFilter && `Year: ${yearFilter}`,
            search && `Search: ${search}`,
        ].filter(Boolean).join("   ·   ");
        return { columns, data, filterLabel };
    };
    const stampDate = () => new Date().toISOString().slice(0, 10);
    const exportExcel = () => { const { columns, data, filterLabel } = buildExport(); exportTicketsExcel({ moduleName: "dSlip", sheetTitle: KIND_LABEL, columns, rows: data, filterLabel, filename: `${kind}_${stampDate()}.xlsx` }); };
    const exportPdf = () => { const { columns, data, filterLabel } = buildExport(); exportTicketsPdf({ moduleName: "dSlip", sheetTitle: KIND_LABEL, columns, rows: data, filterLabel, filename: `${kind}_${stampDate()}.pdf` }); };

    const resetForm = () => {
        setPickEmp(null);
        setPickYear("");
        setPickLetterType(null);
        setFile1(null); setFile2(null); setFile3(null);
    };

    const handlePickFile = (slotSetter, currentFile) => (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_FILE_BYTES) {
            dnAlert.warning("File too large", `Maximum size is 5 MB. "${f.name}" is ${fmtBytes(f.size)}.`);
            // Clear the input so the user can pick again
            e.target.value = "";
            return;
        }
        // Reject a file already sitting in one of the other two slots -
        // uploading the same document twice is never intentional.
        const others = [file1, file2, file3].filter((x) => x !== currentFile);
        if (others.some((x) => sameFile(x, f))) {
            dnAlert.warning("Already added", `"${f.name}" is already attached to another upload slot.`);
            e.target.value = "";
            return;
        }
        slotSetter(f);
        // Allow re-picking the SAME file (useful after a clear) — clears
        // the input value so onChange fires next time too.
        e.target.value = "";
    };

    const handleSave = async () => {
        if (!pickEmp) { dnAlert.warning("Required", "Select an employee."); return; }
        if (!pickYear) { dnAlert.warning("Required", "Select a year."); return; }
        if (hasLetterType && !pickLetterType) { dnAlert.warning("Required", "Select a letter type."); return; }
        if (!file1 && !file2 && !file3) {
            dnAlert.warning("Required", "Pick at least one file before saving.");
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("emp_id", pickEmp.value);
            fd.append("year", pickYear);
            if (hasLetterType && pickLetterType) fd.append("letter_type", pickLetterType.value);
            if (file1) fd.append("file1", file1);
            if (file2) fd.append("file2", file2);
            if (file3) fd.append("file3", file3);

            const res = await apiFetch(`/api/emp_files/${kind}/upload`, {
                method: "POST",
                body: fd,    // browser sets multipart boundary; don't set Content-Type
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                dnAlert.error("Error", data.error || "Upload failed.");
                return;
            }
            dnAlert.success("Uploaded");
            resetForm();
            setShowAdd(false);
            fetchList();
        } catch (err) {
            console.error(err);
            dnAlert.error("Error", "Network error. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // ─── Letter-type management (eletter only) ────────────────────────────
    const handleAddLetterType = async (name) => {
        const t = (name || "").trim();
        if (!t) { dnAlert.warning("Required", "Enter a letter type name."); return false; }
        try {
            const res = await apiFetch("/api/emp_files/letter_types", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type_name: t }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) { dnAlert.error("Error", data.error || "Could not add."); return false; }
            fetchLetterTypes();
            return true;
        } catch { dnAlert.error("Error", "Network error."); return false; }
    };

    const handleRenameLetterType = async (t, newName) => {
        const name = (newName || "").trim();
        if (!name || name === t.type_name) return false;
        if (t.usage_count > 0) {
            const ok = await dnAlert.confirm({
                title: "Rename a letter type that's in use?",
                message: `${t.usage_count} uploaded record${t.usage_count !== 1 ? "s" : ""} already use "${t.type_name}". Renaming this option does NOT change those records - they keep the label "${t.type_name}". New uploads will use "${name}". Continue?`,
                confirmLabel: "Yes, rename",
                danger: true,
            });
            if (!ok) return false;
        }
        try {
            const res = await apiFetch(`/api/emp_files/letter_types/${t.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type_name: name }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) { dnAlert.error("Error", data.error || "Could not rename."); return false; }
            fetchLetterTypes();
            return true;
        } catch { dnAlert.error("Error", "Network error."); return false; }
    };

    const handleToggleLetterType = async (t) => {
        try {
            const res = await apiFetch(`/api/emp_files/letter_types/${t.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ active: t.active ? 0 : 1 }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) { dnAlert.error("Error", data.error || "Could not update."); return; }
            fetchLetterTypes();
        } catch { dnAlert.error("Error", "Network error."); }
    };

    const handleDeleteLetterType = async (t) => {
        const ok = await dnAlert.confirm({
            title: "Delete this letter type?",
            message: t.usage_count > 0
                ? `⚠ "${t.type_name}" is used by ${t.usage_count} uploaded record${t.usage_count !== 1 ? "s" : ""}. Deleting it only removes it from the dropdown - those records KEEP the label "${t.type_name}" and their files are untouched. Continue?`
                : `Remove "${t.type_name}" from the dropdown? This can't be undone.`,
            confirmLabel: "Yes, delete",
            danger: true,
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/emp_files/letter_types/${t.id}`, { method: "DELETE" });
            const data = await res.json();
            if (!res.ok || !data.success) { dnAlert.error("Error", data.error || "Could not delete."); return; }
            dnAlert.success("Removed");
            fetchLetterTypes();
        } catch { dnAlert.error("Error", "Network error."); }
    };

    // ─── Email: notify employees that their document is available ─────────
    // Open the send/remind modal. Fetches a lightweight roster up front (one row
    // per record, no HTML), pre-ticks everyone who can be mailed, and renders the
    // first recipient's actual email on demand — the rest render as the admin
    // pages through, so a 12-person send costs one roster call plus one render
    // per email actually looked at.
    const openEmailModal = async (ids) => {
        if (!ids || ids.length === 0) return;
        setMailFilters({ emp_id: "", year: "" });
        setEmailModal({
            ids, recipients: [], index: 0, previews: {}, checked: [],
            loading: true, previewLoading: false, sending: false, result: null, error: null,
        });
        try {
            const res = await apiFetch(`/api/emp_files/${kind}/email/recipients`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids }),
            });
            const data = await res.json();
            if (!res.ok) { setEmailModal((m) => ({ ...m, loading: false, error: data.error || "Could not build preview." })); return; }
            const recipients = data.recipients || [];
            setEmailModal((m) => ({
                ...m,
                recipients,
                // Everyone who CAN be mailed starts ticked; unticking is how the
                // admin narrows the run. Rows with no address on file are never
                // ticked and cannot be.
                checked: recipients.filter((r) => r.sendable).map((r) => r.id),
                no_email_count: data.no_email_count || 0,
                loading: false,
            }));
            if (recipients.length > 0) loadPreviewAt(0, recipients);
        } catch {
            setEmailModal((m) => ({ ...m, loading: false, error: "Network error." }));
        }
    };

    // Render (and cache) one recipient's email. A cached index short-circuits.
    const loadPreviewAt = async (index, recipientList) => {
        const list = recipientList || emailModal?.recipients || [];
        const r = list[index];
        if (!r) return;
        setEmailModal((m) => (m ? { ...m, index } : m));
        let cached = false;
        setEmailModal((m) => { cached = !!m?.previews?.[r.id]; return m; });
        if (cached) return;
        setEmailModal((m) => (m ? { ...m, previewLoading: true } : m));
        try {
            const res = await apiFetch(`/api/emp_files/${kind}/email/preview`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: r.id }),
            });
            const data = await res.json();
            setEmailModal((m) => m ? {
                ...m, previewLoading: false,
                previews: { ...m.previews, [r.id]: res.ok ? data : { error: data.error || "Could not build preview." } },
            } : m);
        } catch {
            setEmailModal((m) => m ? {
                ...m, previewLoading: false,
                previews: { ...m.previews, [r.id]: { error: "Network error." } },
            } : m);
        }
    };

    const stepPreview = (delta) => {
        if (!emailModal?.recipients?.length) return;
        const next = emailModal.index + delta;
        if (next < 0 || next >= emailModal.recipients.length) return;
        loadPreviewAt(next);
    };

    // Recipient tick helpers. Only `sendable` rows can ever be ticked.
    const toggleRecipient = (id) =>
        setEmailModal((m) => ({
            ...m,
            checked: m.checked.includes(id) ? m.checked.filter((x) => x !== id) : [...m.checked, id],
        }));
    // Select-all applies to the filtered (shown) view only.
    const toggleAllRecipients = (visible) =>
        setEmailModal((m) => {
            const ids = visible.filter((r) => r.sendable).map((r) => r.id);
            const allOn = ids.length > 0 && ids.every((id) => m.checked.includes(id));
            return {
                ...m,
                checked: allOn
                    ? m.checked.filter((id) => !ids.includes(id))
                    : [...new Set([...m.checked, ...ids])],
            };
        });

    // Recipient-picker filter: employee + year. Narrowing only changes what is
    // SHOWN — it never unticks a row, so the footer count stays truthful.
    const passesMailFilter = (r, f) => {
        if (f.emp_id && r.emp_id !== f.emp_id) return false;
        if (f.year && String(r.year) !== String(f.year)) return false;
        return true;
    };
    const mailEmpOptions = (recipients) => {
        const seen = new Map();
        recipients.forEach((r) => { if (!seen.has(r.emp_id)) seen.set(r.emp_id, r.employee_name || r.emp_id); });
        return [
            { value: "", label: `All employees (${seen.size})` },
            ...[...seen].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => ({ value: id, label: `${name} (${id})` })),
        ];
    };
    const mailYearOptions = (recipients) => {
        const years = [...new Set(recipients.map((r) => r.year))].sort((a, b) => b - a);
        return [{ value: "", label: "All years" }, ...years.map((y) => ({ value: String(y), label: String(y) }))];
    };

    const handleSendEmails = async () => {
        const toSend = emailModal?.checked || [];
        if (!emailModal || toSend.length === 0) return;
        setEmailModal((m) => ({ ...m, sending: true }));
        try {
            const res = await apiFetch(`/api/emp_files/${kind}/email/send`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids: toSend }),
            });
            const data = await res.json();
            if (!res.ok) { setEmailModal((m) => ({ ...m, sending: false, result: { error: data.error || "Send failed." } })); return; }
            setEmailModal((m) => ({ ...m, sending: false, result: data }));
            if (data.sent > 0) dnAlert.success("Emails sent", `${data.sent} email${data.sent !== 1 ? "s" : ""} sent.`);
            fetchList();
        } catch {
            setEmailModal((m) => ({ ...m, sending: false, result: { error: "Network error." } }));
        }
    };

    const openEmailHistory = async (row) => {
        setEmailHistory({ scope: "record", row, rows: [], loading: true });
        try {
            const res = await apiFetch(`/api/emp_files/${kind}/email/history/${row.id}`);
            const data = await res.json();
            setEmailHistory({ scope: "record", row, rows: Array.isArray(data) ? data : [], loading: false });
        } catch {
            setEmailHistory({ scope: "record", row, rows: [], loading: false });
        }
    };

    // Page-wide history — every email ever sent from this page (all employees).
    const openPageHistory = async () => {
        setEmailHistory({ scope: "all", row: null, rows: [], loading: true });
        try {
            const res = await apiFetch(`/api/emp_files/${kind}/email/history`);
            const data = await res.json();
            setEmailHistory({ scope: "all", row: null, rows: Array.isArray(data) ? data : [], loading: false });
        } catch {
            setEmailHistory({ scope: "all", row: null, rows: [], loading: false });
        }
    };

    // "Remind All" — email every currently-listed record.
    const handleRemindAll = () => {
        const ids = filteredRows.map((r) => r.id);
        if (ids.length === 0) return;
        openEmailModal(ids);
    };

    const handleDownload = async (id, slot, filename) => {
        try {
            const res = await apiFetch(`/api/emp_files/${kind}/download/${id}/${slot}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                dnAlert.error("Error", j.error || "Could not download file.");
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || `file_${slot}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            dnAlert.error("Error", "Download failed.");
        }
    };

    // ─── Edit mode helpers ───────────────────────────────────────────
    const startEdit = (row) => {
        setEditingId(row.id);
        setEditFiles({ 1: null, 2: null, 3: null });
        setEditClears(new Set());
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditFiles({ 1: null, 2: null, 3: null });
        setEditClears(new Set());
    };

    // Picking a new file for a slot during edit. Re-uses the size check.
    const handleEditPickFile = (slot) => (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_FILE_BYTES) {
            dnAlert.warning("File too large", `Maximum size is 5 MB. "${f.name}" is ${fmtBytes(f.size)}.`);
            e.target.value = "";
            return;
        }
        // Same guard in edit mode: not already picked for another slot, and
        // not a re-upload of a file this row already holds.
        const otherPicks = [1, 2, 3].filter((n) => n !== slot).map((n) => editFiles[n]);
        if (otherPicks.some((x) => sameFile(x, f))) {
            dnAlert.warning("Already added", `"${f.name}" is already attached to another upload slot.`);
            e.target.value = "";
            return;
        }
        const row = rows.find((r) => r.id === editingId);
        const existingNames = [row?.file1_name, row?.file2_name, row?.file3_name].filter(Boolean);
        if (existingNames.includes(f.name)) {
            dnAlert.warning("Already uploaded", `"${f.name}" is already saved on this record. Delete it first if you want to replace it.`);
            e.target.value = "";
            return;
        }
        setEditFiles((p) => ({ ...p, [slot]: f }));
        // Replacing also un-clears (the new file wins).
        setEditClears((p) => {
            const next = new Set(p);
            next.delete(slot);
            return next;
        });
        e.target.value = "";
    };

    // Toggle "delete this slot's existing file" — only meaningful when the
    // slot CURRENTLY has a file and no replacement has been picked yet.
    const toggleClearSlot = (slot) => {
        setEditClears((p) => {
            const next = new Set(p);
            if (next.has(slot)) next.delete(slot);
            else next.add(slot);
            return next;
        });
        // Picking "delete" also drops a previously-picked new file for this slot.
        setEditFiles((p) => ({ ...p, [slot]: null }));
    };

    // Drop a JUST-PICKED replacement file before save (revert to existing).
    const dropNewEditFile = (slot) => {
        setEditFiles((p) => ({ ...p, [slot]: null }));
    };

    const saveEdit = async (row) => {
        // Need at least one slot to end up populated.
        const willHave = [1, 2, 3].map((s) => {
            if (editFiles[s]) return true;
            if (editClears.has(s)) return false;
            return !!row[`file${s}_name`];
        });
        if (!willHave.some(Boolean)) {
            dnAlert.warning("Required", "At least one file must remain on the row.");
            return;
        }
        // Nothing changed → nothing to send.
        const hasChanges = [1, 2, 3].some((s) => editFiles[s] || editClears.has(s));
        if (!hasChanges) {
            dnAlert.info("Nothing changed", "Pick a new file or mark one for delete before saving.");
            return;
        }

        setEditSaving(true);
        try {
            const fd = new FormData();
            [1, 2, 3].forEach((s) => {
                if (editFiles[s]) fd.append(`file${s}`, editFiles[s]);
                if (editClears.has(s)) fd.append(`clear${s}`, "1");
            });
            const res = await apiFetch(`/api/emp_files/${kind}/${row.id}`, {
                method: "PUT",
                body: fd,
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                dnAlert.error("Error", data.error || "Update failed.");
                return;
            }
            dnAlert.success("Updated");
            cancelEdit();
            fetchList();
        } catch (err) {
            console.error(err);
            dnAlert.error("Error", "Network error.");
        } finally {
            setEditSaving(false);
        }
    };

    const handleDelete = async (row) => {
        const empLabel = `${row.emp_first_name} ${row.emp_last_name}`.trim() || row.emp_id;
        const ok = await dnAlert.confirm({
            title: "Delete this entry?",
            message: `${empLabel} · ${row.year}. All files attached to this row will be removed. This cannot be undone.`,
            danger: true,
            confirmLabel: "Delete",
        });
        if (!ok) return;

        try {
            const res = await apiFetch(`/api/emp_files/${kind}/${row.id}`, { method: "DELETE" });
            const data = await res.json();
            if (!res.ok || !data.success) {
                dnAlert.error("Error", data.error || "Delete failed.");
                return;
            }
            dnAlert.success("Deleted");
            fetchList();
        } catch (err) {
            console.error(err);
            dnAlert.error("Error", "Network error.");
        }
    };

    // ─── Render ───────────────────────────────────────────────────────
    return (
        <div className="ef-container">
            <div className={`ef-dashboard ${navSize}`}>
                <div className="ef-content">

                    {/* Header */}
                    <div className="ef-page-header">
                        <div className="ef-page-header-left">
                            <h2>
                                <img src={logo_eagle} alt="dAdmin Logo"
                                     style={{ height: 50, objectFit: "contain", marginLeft: -20, marginRight: 10 }} />
                                {meta.page}
                            </h2>
                            <p className="ef-subtitle">{meta.subtitle}</p>
                        </div>
                    </div>

                    {/* Toolbar — filters + buttons on one flowing row. When there's
                        no data the filters hide and the Add button spans full width. */}
                    <div className={`ef-toolbar ${rows.length === 0 ? "ef-toolbar--empty" : ""}`}>
                        {rows.length > 0 && (
                            <>
                                    <div className="ef-search-wrapper">
                                        <FaSearch className="ef-search-icon" />
                                        <input
                                            type="text"
                                            className="ef-search-input"
                                            placeholder="Search name, ID, year or filename…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <Select
                                        className="ef-filter-select"
                                        options={empFilterOptions}
                                        value={empFilterOptions.find((o) => o.value === empFilter) || empFilterOptions[0]}
                                        onChange={(o) => setEmpFilter(o?.value || "")}
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={{ container: (b) => ({ ...b, width: 230 }), control: (b) => ({ ...b, minHeight: 40 }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                        placeholder="All employees"
                                    />
                                    <Select
                                        className="ef-filter-select"
                                        options={yearFilterOptions}
                                        value={yearFilterOptions.find((o) => o.value === yearFilter) || yearFilterOptions[0]}
                                        onChange={(o) => setYearFilter(o?.value || "")}
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        styles={{ container: (b) => ({ ...b, width: 150 }), control: (b) => ({ ...b, minHeight: 40 }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                        placeholder="All years"
                                    />
                                    <div className="ef-sort-wrap" data-ef-sort-dropdown>
                                        <button
                                            type="button"
                                            className={`ef-sort-btn ${showSortDropdown ? "ef-sort-btn--open" : ""}`}
                                            onClick={() => setShowSortDropdown((p) => !p)}
                                        >
                                            <span className="ef-sort-arrows">↕</span>
                                            {SORT_OPTIONS.find((o) => o.value === sortOrder)?.label || "Sort by…"}
                                            <span className="ef-sort-caret">▼</span>
                                        </button>
                                        {showSortDropdown && (
                                            <div className="ef-sort-menu">
                                                <div className="ef-sort-menu-title">↕ Sort by…</div>
                                                {SORT_OPTIONS.map((opt) => (
                                                    <div
                                                        key={opt.value}
                                                        className={`ef-sort-menu-item ${sortOrder === opt.value ? "ef-sort-menu-item--active" : ""}`}
                                                        onClick={() => {
                                                            setSortOrder(opt.value);
                                                            setShowSortDropdown(false);
                                                        }}
                                                    >{opt.label}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                            </>
                        )}
                        <button
                            className="ef-add-btn"
                            onClick={() => setShowAdd(true)}
                            disabled={saving || showAdd}
                        >
                            <FaPlus /> Add
                        </button>
                            {rows.length > 0 && (
                                <button
                                    className="ef-remind-btn"
                                    onClick={handleRemindAll}
                                    disabled={filteredRows.length === 0}
                                    title="Email every listed employee that their document is available"
                                >
                                    <FaPaperPlane /> Remind All ({filteredRows.length})
                                </button>
                            )}
                        {rows.length > 0 && (
                            <button
                                className="ef-history-btn"
                                onClick={openPageHistory}
                                title="Email History - every document email sent, with full history"
                                aria-label="Email History"
                            >
                                <FaHistory />
                            </button>
                        )}
                        {rows.length > 0 && (
                            <ExportMenu onExcel={exportExcel} onPdf={exportPdf} disabled={!filteredRows.length} />
                        )}
                    </div>

                    {/* Add form — card: header bar (title + gear + Cancel) then a grid of fields */}
                    {showAdd && (
                        <div className="ef-add-card">
                            <div className="ef-add-card-head">
                                <div className="ef-add-card-title">New Entry</div>
                                <div className="ef-add-card-head-actions">
                                    {hasLetterType && (
                                        <button
                                            type="button"
                                            className="ef-btn-options"
                                            onClick={() => setShowManageTypes(true)}
                                            title="Manage letter type dropdown values"
                                        >⚙ Letter Types</button>
                                    )}
                                    <button
                                        type="button"
                                        className="ef-add-card-cancel"
                                        onClick={() => { resetForm(); setShowAdd(false); }}
                                        disabled={saving}
                                    ><FaTimes /> Cancel</button>
                                </div>
                            </div>
                            <div className={`ef-add-grid ${hasLetterType ? "ef-add-grid--eletter" : ""}`}>
                                <div className="ef-field">
                                    <label>Employee</label>
                                <Select
                                    classNamePrefix="ef-rs"
                                    options={empOptions}
                                    value={pickEmp}
                                    onChange={(opt) => {
                                        setPickEmp(opt);
                                        // Drop a previously-picked year if it's
                                        // now before the new emp's DOJ — keeps
                                        // the dropdown in a valid state.
                                        if (opt?.joining_date && pickYear) {
                                            const dojYear = parseInt(opt.joining_date.split("-")[0]);
                                            if (Number.isFinite(dojYear) && parseInt(pickYear) < dojYear) {
                                                setPickYear("");
                                            }
                                        }
                                    }}
                                    isSearchable
                                    placeholder="- select employee -"
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                />
                            </div>
                            <div className="ef-field ef-field--year">
                                <label>
                                    Year
                                    {pickEmp?.joining_date && (
                                        <span className="ef-year-hint">
                                            &nbsp;- DOJ {pickEmp.joining_date.split("-")[0]}
                                        </span>
                                    )}
                                </label>
                                <Select
                                    classNamePrefix="ef-rs"
                                    options={yearOptions.map((y) => ({ value: String(y), label: String(y) }))}
                                    value={pickYear ? { value: String(pickYear), label: String(pickYear) } : null}
                                    onChange={(opt) => setPickYear(opt ? opt.value : "")}
                                    isDisabled={!pickEmp || yearOptions.length === 0}
                                    isSearchable
                                    placeholder={
                                        !pickEmp ? "- pick emp -"
                                            : yearOptions.length === 0 ? "all years used"
                                            : "Year"
                                    }
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    menuPlacement="auto"
                                    maxMenuHeight={240}
                                    styles={EF_YEAR_STYLES}
                                    {...DSLIP_YEAR_SELECT}
                                />
                            </div>

                            {/* Letter Type — eletter only, with a Manage link */}
                            {hasLetterType && (
                                <div className="ef-field">
                                    <label>Letter Type</label>
                                    <Select
                                        classNamePrefix="ef-rs"
                                        options={activeLetterTypeOptions}
                                        value={pickLetterType}
                                        onChange={(opt) => setPickLetterType(opt)}
                                        isSearchable
                                        placeholder={activeLetterTypeOptions.length === 0 ? "- none, add one -" : "- select type -"}
                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                        styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                    />
                                </div>
                            )}

                            {/* Three file slots */}
                            {[
                                { slot: 1, file: file1, setter: setFile1 },
                                { slot: 2, file: file2, setter: setFile2 },
                                { slot: 3, file: file3, setter: setFile3 },
                            ].map(({ slot, file, setter }) => (
                                <div className="ef-field ef-field--upload" key={slot}>
                                    <label>Upload {slot}</label>
                                    <div className="ef-upload-wrap">
                                        <label className={`ef-upload-btn ${file ? "ef-upload-btn--filled" : ""}`}>
                                            {file ? <FaCheckCircle /> : <FaUpload />}
                                            <span className="ef-upload-text">
                                                {file ? file.name : `Choose file`}
                                            </span>
                                            <input
                                                type="file"
                                                onChange={handlePickFile(setter, file)}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                        {file && (
                                            <button
                                                className="ef-upload-clear"
                                                onClick={() => setter(null)}
                                                title="Remove this file"
                                                type="button"
                                            ><FaTimes /></button>
                                        )}
                                    </div>
                                    {file && (
                                        <div className="ef-upload-size">{fmtBytes(file.size)}</div>
                                    )}
                                </div>
                            ))}

                            <div className="ef-field ef-field--save">
                                <label>&nbsp;</label>
                                <button
                                    className="ef-save-btn"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    <FaSave /> {saving ? "Saving…" : "Save"}
                                </button>
                            </div>
                        </div>
                        </div>
                    )}

                    {/* Existing entries table */}
                    {loading ? (
                        <div className="ef-empty">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="ef-empty">
                            <FaFolderOpen size={28} style={{ opacity: 0.5, marginBottom: 8 }} />
                            <div>No entries yet. Click <strong>+ Add</strong> to upload the first set.</div>
                        </div>
                    ) : filteredRows.length === 0 ? (
                        <div className="ef-empty">
                            <FaSearch size={22} style={{ opacity: 0.5, marginBottom: 8 }} />
                            <div>No entries match <strong>"{search}"</strong>.</div>
                        </div>
                    ) : (
                        <div className="ef-table-wrap">
                            <table className="ef-table">
                                <thead>
                                    <tr>
                                        <th>Year</th>
                                        <th>Employee</th>
                                        {hasLetterType && <th>Letter Type</th>}
                                        <th>Uploaded</th>
                                        <th>Downloaded</th>
                                        <th>Emailed</th>
                                        <th>File 1</th>
                                        <th>File 2</th>
                                        <th>File 3</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((r) => {
                                        const empLabel = `${r.emp_first_name} ${r.emp_last_name}`.trim() || r.emp_id;
                                        const isEditing = editingId === r.id;

                                        // Read-only slot cell: just download link or em-dash.
                                        const renderSlotReadOnly = (slot, name) => {
                                            if (!name) return <span className="ef-file-empty">-</span>;
                                            return (
                                                <button
                                                    className="ef-file-link"
                                                    onClick={() => handleDownload(r.id, slot, name)}
                                                    title={`Download ${name}`}
                                                >
                                                    <FaDownload /> {name}
                                                </button>
                                            );
                                        };

                                        // Edit-mode slot cell: shows the current/pending state and
                                        // exposes Replace + Clear actions. Three visual states:
                                        //   a) admin just picked a new file  → green "new" pill + revert
                                        //   b) admin marked for delete       → grey "will delete" pill + undo
                                        //   c) existing file unchanged       → filename + Replace + Trash
                                        //   d) slot is empty                 → "no file" + Replace only
                                        const renderSlotEdit = (slot) => {
                                            const newFile = editFiles[slot];
                                            const willClear = editClears.has(slot);
                                            const existingName = r[`file${slot}_name`];

                                            if (newFile) {
                                                return (
                                                    <div className="ef-slot-edit ef-slot-edit--new">
                                                        <span className="ef-slot-edit-pill ef-slot-edit-pill--new">
                                                            <FaCheckCircle /> {newFile.name}
                                                        </span>
                                                        <button
                                                            className="ef-slot-edit-icon"
                                                            onClick={() => dropNewEditFile(slot)}
                                                            title="Revert to existing file"
                                                            type="button"
                                                        ><FaTimes /></button>
                                                    </div>
                                                );
                                            }
                                            if (willClear && existingName) {
                                                return (
                                                    <div className="ef-slot-edit ef-slot-edit--del">
                                                        <span className="ef-slot-edit-pill ef-slot-edit-pill--del">
                                                            <FaTrash /> {existingName} (will delete)
                                                        </span>
                                                        <button
                                                            className="ef-slot-edit-icon"
                                                            onClick={() => toggleClearSlot(slot)}
                                                            title="Undo delete"
                                                            type="button"
                                                        ><FaTimes /></button>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div className="ef-slot-edit">
                                                    {existingName ? (
                                                        <span className="ef-slot-edit-current">{existingName}</span>
                                                    ) : (
                                                        <span className="ef-file-empty">no file</span>
                                                    )}
                                                    <label className="ef-slot-edit-icon ef-slot-edit-icon--replace" title="Replace this file">
                                                        <FaSyncAlt />
                                                        <input
                                                            type="file"
                                                            onChange={handleEditPickFile(slot)}
                                                            style={{ display: "none" }}
                                                        />
                                                    </label>
                                                    {existingName && (
                                                        <button
                                                            className="ef-slot-edit-icon ef-slot-edit-icon--del"
                                                            onClick={() => toggleClearSlot(slot)}
                                                            title="Delete this file"
                                                            type="button"
                                                        ><FaTrash /></button>
                                                    )}
                                                </div>
                                            );
                                        };

                                        return (
                                            <tr key={r.id} className={isEditing ? "ef-row-editing" : ""}>
                                                <td className="ef-cell-year">{r.year}</td>
                                                <td>
                                                    <div className="ef-emp-cell">
                                                        <div className="ef-avatar">
                                                            {(r.emp_first_name?.[0] || r.emp_id?.[0] || "?").toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="ef-emp-name">{empLabel}</div>
                                                            <div className="ef-emp-id">{r.emp_id}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                {hasLetterType && (
                                                    <td>
                                                        {r.letter_type
                                                            ? <span className="ef-letter-pill">{r.letter_type}</span>
                                                            : <span className="ef-file-empty">-</span>}
                                                    </td>
                                                )}
                                                <td>{fmtDate(r.uploaded_time)}</td>
                                                <td>
                                                    {r.downloaded ? (
                                                        <span title={r.downloaded_at ? `Employee last downloaded: ${new Date(r.downloaded_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}` : ""}
                                                            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 999, background: "#e7f6ec", color: "#15803d", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                                                            <FaCheckCircle /> Downloaded
                                                        </span>
                                                    ) : (
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 999, background: "#eef1f4", color: "#64748b", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                                                            Not yet
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {r.emailed ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => openEmailHistory(r)}
                                                            title={`View send history${r.email_count > 1 ? ` · ${r.email_count} sends` : ""}`}
                                                            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", border: "none", cursor: "pointer", borderRadius: 999, background: "#e7f6ec", color: "#0E7C5A", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                                                            <FaCheckCircle /> Sent
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => openEmailHistory(r)}
                                                            title="View send history"
                                                            style={{ display: "inline-flex", alignItems: "center", padding: "3px 9px", border: "none", cursor: "pointer", borderRadius: 999, background: "#eef1f4", color: "#64748b", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                                                            No mails sent
                                                        </button>
                                                    )}
                                                </td>
                                                {isEditing ? (
                                                    <>
                                                        <td>{renderSlotEdit(1)}</td>
                                                        <td>{renderSlotEdit(2)}</td>
                                                        <td>{renderSlotEdit(3)}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td>{renderSlotReadOnly(1, r.file1_name)}</td>
                                                        <td>{renderSlotReadOnly(2, r.file2_name)}</td>
                                                        <td>{renderSlotReadOnly(3, r.file3_name)}</td>
                                                    </>
                                                )}
                                                <td>
                                                    {isEditing ? (
                                                        <div className="ef-action-group">
                                                            <button
                                                                className="ef-save-btn ef-save-btn--sm"
                                                                onClick={() => saveEdit(r)}
                                                                disabled={editSaving}
                                                            >
                                                                <FaSave /> {editSaving ? "Saving…" : "Save"}
                                                            </button>
                                                            <button
                                                                className="ef-cancel-btn"
                                                                onClick={cancelEdit}
                                                                disabled={editSaving}
                                                                title="Cancel"
                                                            ><FaTimes /></button>
                                                        </div>
                                                    ) : (
                                                        <div className="ef-action-group">
                                                            <button
                                                                className="ef-email-btn"
                                                                onClick={() => openEmailModal([r.id])}
                                                                title="Email this employee that the document is available"
                                                                disabled={editingId !== null}
                                                            ><FaPaperPlane /></button>
                                                            <button
                                                                className="ef-edit-btn"
                                                                onClick={() => startEdit(r)}
                                                                title="Edit row"
                                                                disabled={editingId !== null}
                                                            ><FaEdit /></button>
                                                            <button
                                                                className="ef-delete-btn"
                                                                onClick={() => handleDelete(r)}
                                                                title="Delete entire entry"
                                                                disabled={editingId !== null}
                                                            ><FaTrash /></button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Manage Letter Types modal (eletter only) */}
            {hasLetterType && showManageTypes && (
                <ManageLetterTypesModal
                    types={letterTypes}
                    onClose={() => setShowManageTypes(false)}
                    onAdd={handleAddLetterType}
                    onRename={handleRenameLetterType}
                    onToggle={handleToggleLetterType}
                    onDelete={handleDeleteLetterType}
                />
            )}

            {/* Email preview + send modal */}
            {emailModal && (() => {
                const total = emailModal.recipients?.length || 0;
                const isBulk = total > 1;
                const emSel = {
                    control: (b) => ({ ...b, minHeight: 34 }),
                    menuPortal: (b) => ({ ...b, zIndex: 100000 }),
                };
                const emJump = { ...emSel, container: (b) => ({ ...b, width: 210 }) };
                return (
                <div className="ef-em-overlay" onClick={() => !emailModal.sending && setEmailModal(null)}>
                    <div className={`ef-em-modal${isBulk ? " ef-em-modal--wide" : ""}`} onClick={(e) => e.stopPropagation()}>
                        <div className="ef-em-head">
                            <div>
                                <div className="ef-em-title"><FaEnvelope style={{ marginRight: 8 }} /> {emailModal.ids.length === 1 ? "Send Document Email" : "Remind Employees"}</div>
                                <div className="ef-em-sub">
                                    {total <= 1
                                        ? "1 record · the employee gets their own email"
                                        : <>
                                            {total} recipients
                                            {emailModal.checked && <> · {emailModal.checked.length} selected</>}
                                            {emailModal.no_email_count > 0 && <> · {emailModal.no_email_count} without an email</>}
                                        </>}
                                </div>
                            </div>
                            <button className="ef-em-close" onClick={() => !emailModal.sending && setEmailModal(null)}><FaTimes /></button>
                        </div>
                        <div className="ef-em-body">
                            {emailModal.loading ? (
                                <div className="ef-em-loading">Building preview…</div>
                            ) : emailModal.error ? (
                                <div className="ef-em-error">{emailModal.error}</div>
                            ) : emailModal.result ? (
                                <div className="ef-em-result">
                                    {emailModal.result.error ? (
                                        <div className="ef-em-error">{emailModal.result.error}</div>
                                    ) : (
                                        <>
                                            <div className="ef-em-result-row ef-em-result-sent"><FaCheckCircle /> {emailModal.result.sent} sent</div>
                                            {emailModal.result.failed > 0 && <div className="ef-em-result-row ef-em-result-failed">{emailModal.result.failed} failed</div>}
                                            {emailModal.result.skipped > 0 && <div className="ef-em-result-row ef-em-result-skipped">{emailModal.result.skipped} skipped (no email)</div>}
                                        </>
                                    )}
                                </div>
                            ) : total > 0 ? (() => {
                                const rcpt = emailModal.recipients[emailModal.index] || emailModal.recipients[0];
                                const prev = emailModal.previews?.[rcpt.id];
                                const shownR = emailModal.recipients.filter((r) => passesMailFilter(r, mailFilters));
                                const shownElig = shownR.filter((r) => r.sendable);
                                const shownOn = shownElig.filter((r) => emailModal.checked.includes(r.id));
                                return (
                                    <>
                                        {isBulk && (
                                            <div className="ef-em-pickfilter">
                                                <div className="ef-em-pickfilter-field">
                                                    <label>Employee</label>
                                                    <Select
                                                        options={mailEmpOptions(emailModal.recipients)}
                                                        value={mailEmpOptions(emailModal.recipients).find((o) => o.value === mailFilters.emp_id) || null}
                                                        onChange={(o) => setMailFilters((f) => ({ ...f, emp_id: o?.value || "" }))}
                                                        isSearchable menuPortalTarget={document.body}
                                                        styles={{ ...emSel, container: (b) => ({ ...b, width: 230 }) }}
                                                    />
                                                </div>
                                                <div className="ef-em-pickfilter-field">
                                                    <label>Year</label>
                                                    <Select
                                                        options={mailYearOptions(emailModal.recipients)}
                                                        value={mailYearOptions(emailModal.recipients).find((o) => o.value === mailFilters.year) || null}
                                                        onChange={(o) => setMailFilters((f) => ({ ...f, year: o?.value || "" }))}
                                                        isSearchable={false} menuPortalTarget={document.body}
                                                        styles={{ ...emSel, container: (b) => ({ ...b, width: 120 }) }}
                                                    />
                                                </div>
                                                <span className="ef-em-pickfilter-count">{shownR.length} shown</span>
                                            </div>
                                        )}

                                        {isBulk && (
                                            <div className="ef-em-rcpt-panel">
                                                <div className="ef-em-rcpt-head">
                                                    <label className="ef-em-picker-all">
                                                        <input
                                                            type="checkbox"
                                                            checked={shownElig.length > 0 && shownOn.length === shownElig.length}
                                                            ref={(el) => { if (el) el.indeterminate = shownOn.length > 0 && shownOn.length < shownElig.length; }}
                                                            disabled={shownElig.length === 0}
                                                            onChange={() => toggleAllRecipients(shownR)}
                                                        />
                                                        Select all shown
                                                    </label>
                                                    <span className="ef-em-rcpt-count">{emailModal.checked.length} will be sent</span>
                                                </div>
                                                {shownR.length === 0 ? (
                                                    <div className="ef-em-loading">No recipients match these filters.</div>
                                                ) : (
                                                    <div className="ef-em-rcpt-list">
                                                        <table className="ef-em-picker-table">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: 34 }}></th>
                                                                    <th>Employee</th>
                                                                    <th>Year</th>
                                                                    {hasLetterType && <th>Letter Type</th>}
                                                                    <th>Files</th>
                                                                    <th style={{ width: 44 }}>Preview</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {shownR.map((r) => {
                                                                    const i = emailModal.recipients.indexOf(r);
                                                                    const on = emailModal.checked.includes(r.id);
                                                                    return (
                                                                        <tr
                                                                            key={r.id}
                                                                            className={`${on ? "ef-em-picker-row--on" : ""}${i === emailModal.index ? " ef-em-rcpt-row--active" : ""}${!r.sendable ? " ef-em-rcpt-row--skip" : ""}`}
                                                                            onClick={() => r.sendable && toggleRecipient(r.id)}
                                                                            title={r.sendable ? "Click to include or exclude" : undefined}
                                                                        >
                                                                            <td>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={on}
                                                                                    disabled={!r.sendable}
                                                                                    onChange={() => toggleRecipient(r.id)}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                    title={r.sendable ? "Include in this send" : "No email address on file"}
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                <div className="ef-em-emp-name">{r.employee_name}</div>
                                                                                <div className="ef-em-emp-id">
                                                                                    {r.emp_id}
                                                                                    {!r.sendable && <span className="ef-em-rcpt-skiptag">no email</span>}
                                                                                </div>
                                                                            </td>
                                                                            <td>{r.year}</td>
                                                                            {hasLetterType && <td>{r.letter_type || "-"}</td>}
                                                                            <td>{r.files_count}</td>
                                                                            <td>
                                                                                <button
                                                                                    type="button"
                                                                                    className={`ef-em-icon-btn${i === emailModal.index ? " ef-em-icon-btn--on" : ""}`}
                                                                                    title="Preview this employee's email below"
                                                                                    onClick={(e) => { e.stopPropagation(); loadPreviewAt(i); }}
                                                                                >
                                                                                    <FaEye />
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {isBulk && (
                                            <div className="ef-em-pager">
                                                <button type="button" className="ef-em-pager-btn" onClick={() => stepPreview(-1)} disabled={emailModal.index === 0} title="Previous recipient">‹</button>
                                                <div className="ef-em-pager-mid">
                                                    <span className="ef-em-pager-pos">Recipient {emailModal.index + 1} of {total}</span>
                                                    <span className="ef-em-pager-name">{rcpt.employee_name}<span className="ef-em-pager-meta"> · {rcpt.emp_id} · {rcpt.year}</span></span>
                                                </div>
                                                {(() => {
                                                    const opts = emailModal.recipients.map((r, i) => ({ value: i, label: `${i + 1}. ${r.employee_name}${r.sendable ? "" : " (will skip)"}` }));
                                                    return (
                                                        <Select
                                                            options={opts}
                                                            value={opts[emailModal.index]}
                                                            onChange={(opt) => loadPreviewAt(opt.value)}
                                                            isSearchable menuPortalTarget={document.body} menuPlacement="auto"
                                                            styles={emJump} placeholder="Jump to…"
                                                        />
                                                    );
                                                })()}
                                                <button type="button" className="ef-em-pager-btn" onClick={() => stepPreview(1)} disabled={emailModal.index >= total - 1} title="Next recipient">›</button>
                                            </div>
                                        )}

                                        {!rcpt.sendable && (
                                            <div className="ef-em-warn">No email address on file for <strong>{rcpt.employee_name}</strong> - this one will be skipped.</div>
                                        )}

                                        {emailModal.previewLoading && !prev ? (
                                            <div className="ef-em-loading">Rendering {rcpt.employee_name}'s email…</div>
                                        ) : prev?.error ? (
                                            <div className="ef-em-error">{prev.error}</div>
                                        ) : prev ? (
                                            <>
                                                <div className="ef-em-meta">
                                                    <div className="ef-em-meta-row"><span className="ef-em-meta-label">From</span><span className="ef-em-meta-val">{prev.from}</span></div>
                                                    <div className="ef-em-meta-row"><span className="ef-em-meta-label">To</span><span className="ef-em-meta-val">{prev.to || <em style={{ color: "#dc2626" }}>No email on file</em>}</span></div>
                                                    <div className="ef-em-meta-row"><span className="ef-em-meta-label">Cc</span><span className="ef-em-meta-val">{(prev.cc || []).join(", ")}</span></div>
                                                    <div className="ef-em-meta-row"><span className="ef-em-meta-label">Subject</span><span className="ef-em-meta-val ef-em-meta-subject">{prev.subject}</span></div>
                                                </div>
                                                <iframe title={`Document email preview - ${rcpt.employee_name}`} className="ef-em-iframe" srcDoc={prev.html} />
                                            </>
                                        ) : null}
                                    </>
                                );
                            })() : (
                                <div className="ef-em-loading">No recipients to email.</div>
                            )}
                        </div>
                        <div className="ef-em-foot">
                            {emailModal.result ? (
                                <button className="ef-em-btn ef-em-btn--primary" onClick={() => setEmailModal(null)}>Done</button>
                            ) : (
                                <>
                                    <button className="ef-em-btn ef-em-btn--ghost" onClick={() => setEmailModal(null)} disabled={emailModal.sending}>Cancel</button>
                                    <button
                                        className="ef-em-btn ef-em-btn--primary"
                                        onClick={handleSendEmails}
                                        disabled={emailModal.sending || emailModal.loading || !!emailModal.error || !emailModal.checked?.length}
                                        title={!emailModal.checked?.length ? "Tick at least one recipient" : undefined}
                                    >
                                        <FaPaperPlane style={{ marginRight: 6 }} />
                                        {emailModal.sending ? "Sending…" : `Send (${emailModal.checked?.length || 0})`}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Email history modal */}
            {emailHistory && (
                <div className="ef-em-overlay" onClick={() => setEmailHistory(null)}>
                    <div className="ef-em-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ef-em-head">
                            <div>
                                <div className="ef-em-title"><FaHistory style={{ marginRight: 8 }} /> Email History</div>
                                <div className="ef-em-sub">
                                    {emailHistory.scope === "all"
                                        ? `${TITLES[kind].page} · all sends`
                                        : `${emailHistory.row.emp_first_name} ${emailHistory.row.emp_last_name} · ${emailHistory.row.year}`}
                                </div>
                            </div>
                            <button className="ef-em-close" onClick={() => setEmailHistory(null)}><FaTimes /></button>
                        </div>
                        <div className="ef-em-body">
                            {emailHistory.loading ? (
                                <div className="ef-em-loading">Loading history…</div>
                            ) : emailHistory.rows.length === 0 ? (
                                <div className="ef-em-loading">
                                    {emailHistory.scope === "all"
                                        ? "No emails have been sent from this page yet."
                                        : "No emails have been sent for this record yet."}
                                </div>
                            ) : (
                                <table className="ef-em-hist">
                                    <thead><tr>
                                        <th>Sent At</th>
                                        {emailHistory.scope === "all" && <th>Employee</th>}
                                        <th>To</th><th>Status</th><th>By</th>
                                    </tr></thead>
                                    <tbody>
                                        {emailHistory.rows.map((h) => (
                                            <tr key={h.id}>
                                                <td>{h.created_at ? new Date(h.created_at).toLocaleString("en-IN") : "-"}</td>
                                                {emailHistory.scope === "all" && (
                                                    <td>
                                                        <div>{[h.emp_first_name, h.emp_last_name].filter(Boolean).join(" ") || h.emp_id || "-"}</div>
                                                        <div className="ef-em-hist-cc">
                                                            {h.emp_id}{h.year ? ` · ${h.year}` : ""}{h.letter_type ? ` · ${h.letter_type}` : ""}
                                                        </div>
                                                    </td>
                                                )}
                                                <td>
                                                    <div>{h.to_email}</div>
                                                    {h.cc_email && <div className="ef-em-hist-cc">Cc: {h.cc_email}</div>}
                                                </td>
                                                <td>
                                                    <span className={`ef-em-hist-status ef-em-hist-status--${(h.status || "").toLowerCase()}`}>{h.status}</span>
                                                    {h.error_message && <div className="ef-em-hist-err">{h.error_message}</div>}
                                                </td>
                                                <td>{h.sent_by || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="ef-em-foot">
                            <button className="ef-em-btn ef-em-btn--primary" onClick={() => setEmailHistory(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Manage Letter Types modal (eletter dropdown config) ────────────────────
function ManageLetterTypesModal({ types, onClose, onAdd, onRename, onToggle, onDelete }) {
    const [newName, setNewName] = useState("");
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");

    const submitAdd = async () => {
        const ok = await onAdd(newName);
        if (ok) setNewName("");
    };
    const startEdit = (t) => { setEditId(t.id); setEditName(t.type_name); };
    const submitEdit = async (t) => {
        const ok = await onRename(t, editName);
        if (ok !== false) { setEditId(null); setEditName(""); }
    };

    return (
        <div className="ef-mlt-overlay" onClick={onClose}>
            <div className="ef-mlt-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ef-mlt-head">
                    <div>
                        <div className="ef-mlt-title">Manage Letter Types</div>
                        <div className="ef-mlt-sub">These options fill the Letter Type dropdown. Editing or removing one never changes already-uploaded records.</div>
                    </div>
                    <button className="ef-mlt-close" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="ef-mlt-add">
                    <input
                        className="ef-mlt-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") submitAdd(); }}
                        placeholder="New letter type (e.g. Experience letter)"
                        maxLength={100}
                    />
                    <button className="ef-mlt-add-btn" onClick={submitAdd} disabled={!newName.trim()}>
                        <FaPlus /> Add
                    </button>
                </div>

                <div className="ef-mlt-list">
                    {types.length === 0 ? (
                        <div className="ef-mlt-empty">No letter types yet - add one above.</div>
                    ) : types.map((t) => (
                        <div key={t.id} className={`ef-mlt-row ${t.active ? "" : "ef-mlt-row--inactive"}`}>
                            {editId === t.id ? (
                                <input
                                    className="ef-mlt-input ef-mlt-input--inline"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") submitEdit(t); if (e.key === "Escape") { setEditId(null); } }}
                                    autoFocus
                                    maxLength={100}
                                />
                            ) : (
                                <span className="ef-mlt-name">
                                    {t.type_name}
                                    {!t.active && <span className="ef-mlt-inactive-tag">inactive</span>}
                                </span>
                            )}
                            <span className="ef-mlt-usage">{t.usage_count > 0 ? `${t.usage_count} in use` : "unused"}</span>
                            <div className="ef-mlt-actions">
                                {editId === t.id ? (
                                    <>
                                        <button className="ef-mlt-btn ef-mlt-btn--save" onClick={() => submitEdit(t)}>Save</button>
                                        <button className="ef-mlt-btn" onClick={() => setEditId(null)}>Cancel</button>
                                    </>
                                ) : (
                                    <>
                                        <button className="ef-mlt-icon" onClick={() => startEdit(t)} title="Rename"><FaEdit /></button>
                                        <button className={`ef-mlt-btn ef-mlt-btn--toggle ${t.active ? "" : "ef-mlt-btn--enable"}`} onClick={() => onToggle(t)}>
                                            {t.active ? "Disable" : "Enable"}
                                        </button>
                                        <button className="ef-mlt-icon ef-mlt-icon--del" onClick={() => onDelete(t)} title="Delete"><FaTrash /></button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="ef-mlt-foot">
                    <button className="ef-mlt-done" onClick={onClose}>Done</button>
                </div>
            </div>
        </div>
    );
}
